'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X } from 'lucide-react';
import { useCreateSession } from '@/hooks/use-coach-data';
import { useConvexMutation } from '@/hooks/use-convex-mutation';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import {
  SESSION_TEMPLATES,
  BALL_LEVEL_COLOURS,
  type SessionTemplate,
} from '@/shared/sessionTemplates';

interface SessionFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDate?: string;
  preselectedTemplate?: SessionTemplate;
  onSuccess?: () => void;
}

const COURT_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];

function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function SessionFormModal({
  open,
  onOpenChange,
  defaultDate,
  preselectedTemplate,
  onSuccess,
}: SessionFormModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTemplate | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [sessionType, setSessionType] = useState('group');
  const [courtNumber, setCourtNumber] = useState('');
  const [error, setError] = useState('');

  const startTimeRef = useRef<HTMLInputElement>(null);
  const createSession = useCreateSession();
  const saveNote = useConvexMutation(api.coach.notes.save);

  const isQuickStart = !!preselectedTemplate;

  useEffect(() => {
    if (open) {
      const template = preselectedTemplate ?? null;
      setSelectedTemplate(template);
      setShowPicker(false);
      setDate(defaultDate || new Date().toISOString().split('T')[0]);
      setStartTime('');
      setEndTime('');
      setSessionType(template?.sessionType ?? 'group');
      setCourtNumber('');
      setError('');

      // Focus start time after dialog animation settles
      if (isQuickStart) {
        setTimeout(() => startTimeRef.current?.focus(), 100);
      }
    }
  }, [open, defaultDate, preselectedTemplate, isQuickStart]);

  const handleSelectTemplate = (template: SessionTemplate) => {
    setSelectedTemplate(template);
    setShowPicker(false);
    setSessionType(template.sessionType);
    if (startTime) {
      setEndTime(addMinutes(startTime, template.durationMinutes));
    }
  };

  const handleClearTemplate = () => {
    setSelectedTemplate(null);
    setShowPicker(false);
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (selectedTemplate && value) {
      setEndTime(addMinutes(value, selectedTemplate.durationMinutes));
    }
  };

  const validateForm = (): boolean => {
    if (!date) { setError('Date is required'); return false; }
    if (!startTime) { setError('Start time is required'); return false; }
    if (!endTime) { setError('End time is required'); return false; }
    if (startTime >= endTime) { setError('End time must be after start time'); return false; }
    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const sessionId = await createSession.mutateAsync({
        date,
        startTime: startTime + ':00',
        endTime: endTime + ':00',
        sessionType,
        courtNumber: courtNumber ? parseInt(courtNumber, 10) : undefined,
      });

      if (selectedTemplate && sessionId) {
        const noteText =
          `📋 ${selectedTemplate.name} — ${selectedTemplate.ageRange}\n\n` +
          selectedTemplate.suggestedActivities.map((a) => `• ${a}`).join('\n');
        await saveNote.mutateAsync({
          sessionId: sessionId as Id<'trainingSessions'>,
          note: noteText,
        });
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    }
  };

  const isPending = createSession.isPending || saveNote.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isQuickStart ? 'Quick Start Session' : 'Create New Session'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* ── Template section ── */}
          {isQuickStart && !showPicker ? (
            /* Quick-start mode: compact badge */
            selectedTemplate ? (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${BALL_LEVEL_COLOURS[selectedTemplate.ballLevel].dot}`} />
                <span className="text-sm font-medium flex-1">{selectedTemplate.name}</span>
                <span className="text-xs text-muted-foreground">{selectedTemplate.ageRange} · {selectedTemplate.durationMinutes} min</span>
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5 ml-1"
                >
                  <X className="w-3 h-3" />
                  Change
                </button>
              </div>
            ) : null
          ) : (
            /* Full template picker (default form or after tapping "Change") */
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  Start from a template{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                {showPicker && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPicker(false);
                      // Restore the preselected template if the user cancels the change
                      if (preselectedTemplate) setSelectedTemplate(preselectedTemplate);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-2">
                {SESSION_TEMPLATES.map((t) => {
                  const colours = BALL_LEVEL_COLOURS[t.ballLevel];
                  const isSelected = selectedTemplate?.id === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => isSelected ? handleClearTemplate() : handleSelectTemplate(t)}
                      className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colours.dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{t.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {t.ageRange} · {t.durationMinutes} min
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.description}</p>
                        </div>
                        {isSelected && (
                          <span className="text-xs text-primary font-medium flex-shrink-0">Selected</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedTemplate && (
                <p className="text-xs text-muted-foreground">
                  Session type and duration pre-filled. Activity plan will be added to session notes automatically.
                </p>
              )}
            </div>
          )}

          <div className={isQuickStart && !showPicker ? '' : 'border-t pt-4'}>
            <div className="space-y-4">
              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>

              {/* Times */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startTime">Start Time *</Label>
                  <Input
                    id="startTime"
                    ref={startTimeRef}
                    type="time"
                    value={startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endTime">End Time *</Label>
                  <Input
                    id="endTime"
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Type + Court */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sessionType">Session Type</Label>
                  <Select value={sessionType} onValueChange={setSessionType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="group">Group Session</SelectItem>
                      <SelectItem value="private">Private Lesson</SelectItem>
                      <SelectItem value="clinic">Clinic</SelectItem>
                      <SelectItem value="match_play">Match Play</SelectItem>
                      <SelectItem value="cardio_tennis">Cardio Tennis</SelectItem>
                      <SelectItem value="squads">Squads</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="courtNumber">Court Number</Label>
                  <Select value={courtNumber} onValueChange={setCourtNumber}>
                    <SelectTrigger>
                      <SelectValue placeholder="No court" />
                    </SelectTrigger>
                    <SelectContent>
                      {COURT_NUMBERS.map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          Court {num}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Creating...' : 'Create Session'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
